/**
 * Lightweight, privacy-light page-view tracking. Fires a best-effort beacon to the
 * public ingest endpoint (POST /api/analytics/pageview) on every route change, for both
 * anonymous and logged-in visitors. Powers the admin Traffic dashboard.
 *
 * - visitorId: a persistent anonymous id (localStorage) — counts unique visitors.
 * - sessionId: a 30-minute sliding-window id — counts sessions.
 * - userId: sent when logged in so the admin can split authed vs anonymous traffic.
 * The server stamps country (offline geoip), device, and referrer host.
 */

const VID_KEY = 'ug_vid';
const SID_KEY = 'ug_sid';
const SID_TS_KEY = 'ug_sid_ts';
const SESSION_TTL_MS = 30 * 60 * 1000;

// document.referrer + UTM params are only meaningful on the first view of a fresh
// document load (the landing). SPA navigations keep the original external referrer /
// drop the query, so we only send acquisition signals on the first beacon.
let firstView = true;

/** Parse utm_source/medium/campaign from the landing URL (first view only). */
function getUtm(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  try {
    const p = new URLSearchParams(window.location.search);
    const clip = (v: string | null) => (v ? v.slice(0, 100) : undefined);
    return {
      utmSource: clip(p.get('utm_source')),
      utmMedium: clip(p.get('utm_medium')),
      utmCampaign: clip(p.get('utm_campaign')),
    };
  } catch {
    return {};
  }
}

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `v-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function getVisitorId(): string {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) {
      v = uuid();
      localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch {
    return 'anon';
  }
}

function getSessionId(): string {
  try {
    const now = Date.now();
    const ts = parseInt(localStorage.getItem(SID_TS_KEY) || '0', 10);
    let sid = localStorage.getItem(SID_KEY);
    if (!sid || !ts || now - ts > SESSION_TTL_MS) {
      sid = uuid();
      localStorage.setItem(SID_KEY, sid);
    }
    localStorage.setItem(SID_TS_KEY, String(now));
    return sid;
  } catch {
    return 'anon';
  }
}

export function trackPageView(path: string, userId?: string | null): void {
  if (typeof window === 'undefined' || !path) return;
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'https://api.ultragoon.co';
    const utm = firstView ? getUtm() : {};
    const body = JSON.stringify({
      path,
      referrer: firstView ? document.referrer || '' : '',
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
      userId: userId || undefined,
      ...utm,
    });
    firstView = false;
    // keepalive so the request survives an immediate navigation/unload.
    fetch(`${apiUrl}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* best-effort — tracking must never affect the page */
    });
  } catch {
    /* ignore */
  }
}
